import React, { useEffect, useMemo } from 'react';
import sChatMessage from '../ChatMessage.module.scss';
import s from './AssistantChatMessage.module.scss';
import { ChatMessage } from '../ChatMessage';
import { ChatMessageWidget, Theme } from '../../../types';
import { useHeightObserver } from './useHeightObserver';
import { useDataHooks } from '@wix/data-hooks-react';
import { assistantChatMessageDataHooks } from './AssistantChatMessage.dataHooks';
import cs from 'classnames';
import { parseChatMessage } from '../../../MessageInterperter';
import { LoadingAssistantMessage } from '../LoadingAssistantMessage/LoadingAssistantMessage';
import { Box } from '@wix/design-system';
import { TextChatWidget } from '../../ChatMessageWidgets/TextChatWidget';
import { WithDisabledWidget } from './WithDisabledWidget';
import { type OnWidgetAction } from '@wix/chat-widget-common-widgets';
import { ErrorBoundary } from './ErrorBoundary';
import { ErrorWidget } from '../../ChatMessageWidgets/ErrorWidget';

interface AssistantChatMessageProps {
  dataHook?: string;
  content: string;
  onTypingEnd?: () => void;
  onTyping?: () => void;
  widgets: ChatMessageWidget[];
  onNewLine?: () => void;
  index: number;
  statusMessage?: string;
  assistantName?: string;
  theme: Theme;
  isLast: boolean;
  onWidgetAction: OnWidgetAction;
  onError: (error: Error) => void;
}

export const AssistantChatMessage: React.FC<AssistantChatMessageProps> = ({
  dataHook,
  content,
  onTypingEnd = () => {},
  onTyping = () => {},
  onNewLine = () => {},
  widgets,
  index,
  statusMessage,
  assistantName = '',
  theme,
  isLast,
  onWidgetAction,
  onError = () => {},
}) => {
  const dataHooks = useDataHooks(assistantChatMessageDataHooks, dataHook);
  const messageRef = React.useRef<HTMLDivElement>(null);
  const messageBgRef = React.useRef<HTMLDivElement>(null);
  useHeightObserver(messageRef, messageBgRef, onNewLine);
  const [isFinishedTyping, setIsFinishedTyping] = React.useState(false);

  const typingEndHandler = () => {
    setIsFinishedTyping(true);
    onTypingEnd?.();
  };

  const parsedWidgets = useMemo(() => {
    try {
      return parseChatMessage(content.toString(), widgets);
    } catch (e) {
      onError(e as Error);
      return [];
    }
  }, [content, widgets, onError]);

  const nonTextualParsedWidget = parsedWidgets.find(
    ({ component }) => !!component,
  );
  const nonTextualWidget =
    nonTextualParsedWidget?.component &&
    nonTextualParsedWidget.component(nonTextualParsedWidget.data);

  let isLoading = false;
  const textualWidget = parsedWidgets
    .filter(({ component }) => !component)
    .flatMap(({ data }) => {
      if (!data?.message) {
        isLoading = true;

        return (
          <LoadingAssistantMessage
            // dataHook={dataHooks.loadingAnimation()}
            statusMessage={statusMessage}
            theme={theme}
          />
        );
      }

      return (
        <TextChatWidget
          data={{
            text: data.message,
            useTyper: true,
            onTypingEnd: typingEndHandler,
            onTyping,
          }}
        />
      );
    })[0];

  const showWidget = nonTextualWidget && (isFinishedTyping || !textualWidget);

  // In case there is no textual component and only a widget, we need to call onTypingEnd here.
  useEffect(() => {
    if (!textualWidget && showWidget) {
      onTypingEnd();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWidget]);

  return (
    <>
      {textualWidget && (
        <div
          className={cs(s.messageAiWrapper, {
            [s.messageAiWrapperFirst]: index === 0,
          })}
          ref={messageRef}
          data-hook={dataHooks.base()}
        >
          <div className={s.messageAiBg} ref={messageBgRef}></div>
          <ChatMessage
            dataHook={dataHooks.chatMessage()}
            className={sChatMessage.messageAi}
            img={<div className={sChatMessage.aiAvatarImage} />}
            name={String(assistantName)}
            theme={theme}
            isLoading={isLoading && theme !== 'original'}
            isAssistant
          >
            {textualWidget}
          </ChatMessage>
        </div>
      )}
      {showWidget && (
        <div
          className={cs(s.messageAiWrapper, {
            [s.messageAiWrapperFirst]: index === 0,
          })}
          ref={messageRef}
          data-hook={dataHooks.base()}
        >
          <div className={s.messageAiBg}></div>
          <ChatMessage
            dataHook={dataHooks.chatMessage()}
            className={sChatMessage.messageAi}
            img={<div className={sChatMessage.aiAvatarImage} />}
            name={String(assistantName)}
            theme={theme}
            isWidget
          >
            <Box className={sChatMessage.widgets} direction="vertical">
              <ErrorBoundary fallback={<ErrorWidget />} onError={onError}>
                <WithDisabledWidget
                  shouldBeDisabled={!isLast}
                  widgetName={nonTextualParsedWidget.widget}
                  onWidgetAction={onWidgetAction}
                >
                  {nonTextualWidget}
                </WithDisabledWidget>
              </ErrorBoundary>
            </Box>
          </ChatMessage>
        </div>
      )}
    </>
  );
};
